import { useTranslations } from '@storebrand-digital/fms-translations-react';
import { format, set, setHours } from 'date-fns';
import { HourPicker } from './HourPicker';
import { FieldProps, StringSchema } from '../types';
import { joinErrors } from '../validation/utils';
import { hasErrors } from '../validation/validateSchema';
import { Label } from '../../shared/components/Label/Label';

const formatDate = (date: Date) => format(date, "yyyy-MM-dd'T'HH:mm:ss");

const getBaseDate = () => {
  return set(new Date(), {
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0
  });
};

const convertTimeStringToDate = (value: string | null | void) => {
  if (value == null || value === '') {
    return null;
  }
  const hourValue = Number.parseInt(value, 10);
  return setHours(getBaseDate(), hourValue);
};

const convertDateToTimeString = (value: Date | null) => {
  const baseDateWithHourValue = value
    ? setHours(getBaseDate(), value.getHours())
    : null;
  return baseDateWithHourValue === null
    ? ''
    : formatDate(baseDateWithHourValue).substring(11);
};

type TimeValue = string | null;

type HourFieldProps = FieldProps<StringSchema, TimeValue>;

export const HourField = (props: HourFieldProps) => {
  const {
    id,
    schema,
    name,
    value,
    onChange,
    onBlur,
    touched,
    forceShowError,
    errors,
    readOnly
  } = props;

  const { getText } = useTranslations();

  const handleChange = (date: Date | null) => {
    onChange({
      target: {
        name,
        value: convertDateToTimeString(date)
      }
    });
  };

  const handleBlur = () => {
    onBlur({
      target: {
        name,
        touched: true
      }
    });
  };

  const showError = !!((touched || forceShowError) && hasErrors(errors));

  const error = showError ? joinErrors(errors, getText) : undefined;

  const dateValue = convertTimeStringToDate(value);

  const infoPopoverId = schema.description && `${id}`;

  const label = (
    <Label
      title={schema.title}
      description={schema.description}
      id={infoPopoverId}
    />
  );

  return (
    <HourPicker
      id={`${id}_time`}
      name={name}
      label={label}
      aria-describedby={`${infoPopoverId}_description`}
      onChange={handleChange}
      onBlur={handleBlur}
      value={dateValue}
      hint={schema.$uiOptions?.hint}
      error={error}
      disabled={readOnly}
    />
  );
};
